import { useCallback, useEffect, useState } from 'react';
import { upi, consentToProposal, narrowMandate, rupees, INSTRUMENT_LABELS, type Mandate, type Proposal } from '../../api/upi';
import UPIStatusBadge from './UPIStatusBadge';
import SignWithDialog from './SignWithDialog';
import ProposeMandateDialog from './ProposeMandateDialog';
import MandateTermsFields, { constraintsFromTerms, termsFromConstraints, type TermsForm } from './MandateTermsFields';
import { describeTerms, formatDate, shortDid } from './terms';

interface Props {
  role: 'customer' | 'agent';
  did: string;
}

function SpendBar({ label, spent, limit }: { label: string; spent: number; limit: unknown }) {
  if (limit === undefined) return null;
  const cap = Number(limit);
  const pct = cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 100;
  return (
    <div>
      <div className="flex justify-between text-[11px] text-text-muted">
        <span>{label}</span>
        <span>{rupees(spent)} of {rupees(cap)}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100">
        <div className={`h-1.5 rounded-full ${pct >= 90 ? 'bg-red-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TermsList({ constraints }: { constraints: Record<string, unknown> }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {describeTerms(constraints).map((t) => (
        <li key={t} className="rounded-md bg-slate-50 px-2 py-0.5 text-[11px] text-text-main">{t}</li>
      ))}
    </ul>
  );
}

export default function MandatesTab({ role, did }: Props) {
  const [mandates, setMandates] = useState<Mandate[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consenting, setConsenting] = useState<Proposal | null>(null);
  const [narrowing, setNarrowing] = useState<Mandate | null>(null);
  const [narrowTerms, setNarrowTerms] = useState<TermsForm | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);

  const who = role === 'customer' ? { customer: did } : { agent: did };
  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([upi.mandates(who), upi.proposals(who)])
      .then(([m, p]) => { setMandates(m); setProposals(p); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    // who is derived from role and did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, did]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const open = proposals.filter((p) => p.status === 'proposed');
  const closed = proposals.filter((p) => p.status !== 'proposed');

  return (
    <div className="space-y-6">
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {role === 'agent' && (
        <div className="flex justify-end">
          <button id="propose-mandate" onClick={() => setProposeOpen(true)} className="btn-primary">Propose a mandate</button>
        </div>
      )}

      {open.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-text-faint">
            {role === 'customer' ? 'Waiting for your answer' : 'Waiting for the customer'}
          </h3>
          {open.map((p) => (
            <div key={p.id} className="rounded-xl border border-amber-200 bg-amber-50/40 p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-text-main">{p.purpose}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-text-muted">
                    {role === 'customer' ? `Agent ${shortDid(p.agentDid)}` : `Customer ${shortDid(p.customerDid)}`}
                  </p>
                </div>
                <UPIStatusBadge status={p.status} label={role === 'agent' ? 'Sent' : undefined} />
              </div>
              <div className="mt-3"><TermsList constraints={p.constraints} /></div>
              <p className="mt-2 text-[11px] text-text-muted">
                {INSTRUMENT_LABELS[p.instrument.kind] ?? p.instrument.kind} · until {formatDate(p.validUntil)} · answer by {formatDate(p.expiresAt)}
              </p>
              <div className="mt-3 flex gap-2">
                {role === 'customer' ? (
                  <>
                    <button onClick={() => setConsenting(p)} className="btn-primary text-xs">Review and sign</button>
                    <button onClick={() => void act(() => upi.decline(p.id))} className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-medium hover:bg-slate-50">Decline</button>
                  </>
                ) : (
                  <button onClick={() => void act(() => upi.withdraw(p.id))} className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-medium hover:bg-slate-50">Withdraw</button>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-text-faint">Mandates</h3>
        {loading && <p className="text-sm text-text-muted">Loading…</p>}
        {!loading && mandates.length === 0 && (
          <div className="rounded-xl border border-dashed border-border-subtle p-8 text-center text-sm text-text-muted">
            {role === 'customer' ? (
              <>
                <p>You have not given any agent a payment mandate.</p>
                <p className="mt-2 text-xs">
                  A mandate starts as a request from the agent’s operator, which appears here for you to sign. If you operate the agent,
                  choose it under Showing and use Propose a mandate. The agent needs a bank instrument with you first.
                </p>
              </>
            ) : 'This agent holds no payment mandate. Propose one to a customer.'}
          </div>
        )}
        {mandates.map((m) => (
          <div key={m.id} className="rounded-xl border border-border-subtle bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-text-main">{m.consent?.purpose || 'Payment mandate'}</p>
                <p className="mt-0.5 font-mono text-[11px] text-text-muted">
                  {role === 'customer' ? `Agent ${shortDid(m.agentDid)}` : `Customer ${shortDid(m.customerDid)}`} · {shortDid(m.id)}
                </p>
              </div>
              <UPIStatusBadge status={m.status} />
            </div>
            <div className="mt-3"><TermsList constraints={m.constraints} /></div>
            {m.status === 'active' && m.spend && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <SpendBar label="Today" spent={m.spend.daySpentPaise} limit={m.constraints.maxPerDayPaise} />
                <SpendBar label="This period" spent={m.spend.periodSpentPaise} limit={m.constraints.maxPerPeriodPaise} />
              </div>
            )}
            {m.reason && m.status !== 'active' && (
              <p className="mt-2 text-xs text-text-muted">{m.status === 'superseded' ? `Replaced by a narrower mandate ${shortDid(m.supersededBy ?? '')}` : m.reason}</p>
            )}
            <p className="mt-2 text-[11px] text-text-muted">
              {INSTRUMENT_LABELS[m.instrument.kind] ?? m.instrument.kind} · {m.instrument.ref} · valid {formatDate(m.validFrom)} to {formatDate(m.validUntil)}
            </p>
            {m.consent && (
              <p className="mt-1 text-[11px] text-text-faint" title={m.consent.termsHash}>
                Consent given {formatDate(m.consent.givenAt)}
                {m.consent.withdrawnAt ? ` · withdrawn ${formatDate(m.consent.withdrawnAt)}` : ''}
              </p>
            )}
            {role === 'customer' && m.status === 'active' && (
              <div className="mt-3 flex gap-2">
                <button onClick={() => { setNarrowing(m); setNarrowTerms(termsFromConstraints(m.constraints)); }} className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-medium hover:bg-slate-50">
                  Tighten limits
                </button>
                <button
                  onClick={() => { if (confirm('Revoke this mandate? The agent can no longer pay with it.')) void act(() => upi.revoke(m.id, 'revoked by the customer')); }}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                >
                  Revoke
                </button>
              </div>
            )}
          </div>
        ))}
      </section>

      {closed.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-text-faint">Earlier proposals</h3>
          {closed.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-border-subtle bg-white px-4 py-2 text-xs">
              <span className="text-text-main">{p.purpose}</span>
              <UPIStatusBadge status={p.status} />
            </div>
          ))}
        </section>
      )}

      {consenting && (
        <SignWithDialog
          open
          title="Give this agent a payment mandate"
          did={consenting.customerDid}
          action="Sign and give consent"
          onClose={() => setConsenting(null)}
          onSign={async (signer) => { await consentToProposal(consenting, signer); load(); }}
        >
          <div className="rounded-lg border border-border-subtle bg-slate-50 p-4">
            <p className="font-semibold text-text-main">{consenting.purpose}</p>
            <p className="mt-1 font-mono text-[11px] text-text-muted">Agent {consenting.agentDid}</p>
            <div className="mt-3"><TermsList constraints={consenting.constraints} /></div>
            <p className="mt-2 text-[11px] text-text-muted">
              Paid from your {INSTRUMENT_LABELS[consenting.instrument.kind] ?? consenting.instrument.kind}, until {formatDate(consenting.validUntil)}.
              Your bank still checks every payment, and you can revoke this at any time.
            </p>
          </div>
        </SignWithDialog>
      )}

      {narrowing && narrowTerms && (
        <SignWithDialog
          open
          title="Tighten this mandate"
          did={narrowing.customerDid}
          action="Sign the tighter mandate"
          onClose={() => setNarrowing(null)}
          onSign={async (signer) => {
            const { constraints, error: e } = constraintsFromTerms(narrowTerms);
            if (e || !constraints) throw new Error(e);
            for (const k of ['allowedMCCs', 'timeWindow', 'timezone']) {
              if (narrowing.constraints[k] !== undefined) constraints[k] = narrowing.constraints[k];
            }
            await narrowMandate(narrowing, constraints, undefined, signer);
            load();
          }}
        >
          <p className="text-text-muted">
            The new mandate replaces this one. It can only be tighter, and what you have spent today and this period still counts.
          </p>
          <MandateTermsFields value={narrowTerms} onChange={setNarrowTerms} />
        </SignWithDialog>
      )}

      {role === 'agent' && <ProposeMandateDialog open={proposeOpen} agentDid={did} onClose={() => setProposeOpen(false)} onProposed={load} />}
    </div>
  );
}
